import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/ContactForm.css';

interface FilePickerProps {
  id: string;
  name: string;
  label: string;
  required?: boolean;
  multiple?: boolean;
  accept?: string;
  helpText?: string;
  value: FileList | null;
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
  maxFiles?: number;
  maxTotalSize?: number; // in bytes
}

const FilePicker: React.FC<FilePickerProps> = ({
  id,
  name,
  label,
  required = false,
  multiple = false,
  accept = 'image/jpeg,image/png,image/jpg,application/pdf',
  helpText,
  value,
  error,
  onChange,
  onRemove,
  placeholder,
  maxFiles,
  maxTotalSize
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | undefined>();

  // Clear file input when value becomes null/empty
  useEffect(() => {
    if (!value && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      setLocalError(undefined);
      onChange(e);
      return;
    }

    const fileArray = Array.from(files);
    let validationError: string | undefined;

    // Check max files limit
    if (maxFiles !== undefined && fileArray.length > maxFiles) {
      validationError = t('form.filePicker.maxFilesError', { maxFiles }) || 
        `Maximum ${maxFiles} file(s) allowed.`;
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setLocalError(validationError);
      return;
    }

    // Check max total size limit
    if (maxTotalSize !== undefined) {
      const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > maxTotalSize) {
        const maxSizeMB = Math.round(maxTotalSize / (1024 * 1024));
        validationError = t('form.filePicker.maxTotalSizeError', { maxSizeMB }) || 
          `Total file size must not exceed ${maxSizeMB}MB.`;
        // Reset input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setLocalError(validationError);
        return;
      }
    }

    // If we have existing files and adding new ones, check combined limits
    if (value && value.length > 0) {
      const existingFiles = Array.from(value);
      const combinedFiles = [...existingFiles, ...fileArray];

      // Check combined file count
      if (maxFiles !== undefined && combinedFiles.length > maxFiles) {
        validationError = t('form.filePicker.maxFilesError', { maxFiles }) || 
          `Maximum ${maxFiles} file(s) allowed.`;
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setLocalError(validationError);
        return;
      }

      // Check combined total size
      if (maxTotalSize !== undefined) {
        const existingTotalSize = existingFiles.reduce((sum, file) => sum + file.size, 0);
        const newTotalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
        if (existingTotalSize + newTotalSize > maxTotalSize) {
          const maxSizeMB = Math.round(maxTotalSize / (1024 * 1024));
          validationError = t('form.filePicker.maxTotalSizeError', { maxSizeMB }) || 
            `Total file size must not exceed ${maxSizeMB}MB.`;
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setLocalError(validationError);
          return;
        }
      }
    }

    // All validations passed
    setLocalError(undefined);
    onChange(e);
  };

  const fileCount = value ? value.length : 0;
  const hasFiles = fileCount > 0;
  const defaultPlaceholder = placeholder || t('form.filePicker.placeholder');
  const requiredLabel = t('accessibility.required');
  const displayError = error || localError;

  return (
    <div className="form-group file-picker-group">
      <label htmlFor={id}>
        {label} {required && <span aria-label={requiredLabel}>*</span>}
      </label>
      <div className="file-input-wrapper">
        <input
          ref={fileInputRef}
          type="file"
          id={id}
          name={name}
          onChange={handleFileChange}
          required={required}
          multiple={multiple}
          accept={accept}
          aria-describedby={`${id}-help ${displayError ? `${id}-error` : ''}`.trim()}
          aria-invalid={!!displayError}
          className="file-input-hidden"
        />
        <label htmlFor={id} className="file-input-button">
          <span className="file-text">
            {hasFiles ? t('form.filePicker.filesSelected', { count: fileCount }) : defaultPlaceholder}
          </span>
        </label>
        {hasFiles && (
          <div className="selected-files" role="status" aria-live="polite">
            {Array.from(value!).map((file, index) => (
              <div key={index} className="selected-file">
                <span className="file-name">{file.name}</span>
                <span className="file-size">({Math.round(file.size / 1024)} KB)</span>
                <button
                  type="button"
                  className="file-remove-button"
                  onClick={() => onRemove(index)}
                  aria-label={t('form.filePicker.removeFile', { fileName: file.name })}
                  title={t('form.filePicker.removeFile', { fileName: file.name })}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {helpText && (
        <small id={`${id}-help`} className="file-help">
          {helpText}
        </small>
      )}
      {displayError && (
        <div id={`${id}-error`} className="form-error" role="alert">
          {displayError}
        </div>
      )}
    </div>
  );
};

export default FilePicker;
